/*
 * /sonar-depth-map — the bottom in 3D (three.js r152, loaded only when the
 * visitor opens this view).
 *
 *   SDM3D.open(host, res, {palette, exaggeration, waterLabel}) → view
 *   view.update(res, opts) · view.setExaggeration(z) · view.resize() · view.dispose()
 *
 * The grid is thinned to ≤ 360 nodes a side; a quad is drawn only where all
 * four corners have data, so the model has the map's own holes instead of a
 * bottom invented between passes. Depth is stretched (default ×6): a lake is
 * hundreds of metres wide and a few metres deep, and at true scale the
 * bottom reads as a flat sheet. Rendering is on demand — a still model costs
 * nothing.
 */
(function (root) {
    'use strict';

    function build(THREE, res, opts) {
        var g = res.geo, D = res.depth;
        var step = Math.max(1, Math.ceil(Math.max(g.w, g.h) / 360));
        var w = Math.floor((g.w - 1) / step) + 1, h = Math.floor((g.h - 1) / step) + 1;
        var pal = root.SDMRender.palette(opts.palette), rg = root.SDMRender.range(res), span = rg[1] - rg[0];
        var pos = new Float32Array(w * h * 3), col = new Float32Array(w * h * 3), valid = new Uint8Array(w * h);
        var cx = (g.w - 1) * g.cell / 2, cz = (g.h - 1) * g.cell / 2;
        for (var r = 0; r < h; r++) {
            for (var c = 0; c < w; c++) {
                var gi = (r * step) * g.w + c * step, i = r * w + c, d = D[gi];
                pos[i * 3] = c * step * g.cell - cx;
                pos[i * 3 + 2] = r * step * g.cell - cz;
                if (d === d) {
                    valid[i] = 1;
                    pos[i * 3 + 1] = -d;
                    var rgb = pal((d - rg[0]) / span);
                    col[i * 3] = rgb[0] / 255; col[i * 3 + 1] = rgb[1] / 255; col[i * 3 + 2] = rgb[2] / 255;
                }
            }
        }
        var idx = [];
        for (var r2 = 0; r2 < h - 1; r2++) {
            for (var c2 = 0; c2 < w - 1; c2++) {
                var a = r2 * w + c2, b = a + 1, cc = a + w, dd = cc + 1;
                if (!(valid[a] && valid[b] && valid[cc] && valid[dd])) continue;
                idx.push(a, cc, b, b, cc, dd);
            }
        }
        var geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        geom.setAttribute('color', new THREE.BufferAttribute(col, 3));
        geom.setIndex(idx.length > 65535 ? new THREE.BufferAttribute(new Uint32Array(idx), 1) : new THREE.BufferAttribute(new Uint16Array(idx), 1));
        geom.computeVertexNormals();
        return { geom: geom, sizeX: (g.w - 1) * g.cell, sizeZ: (g.h - 1) * g.cell, depth: rg[1] };
    }

    function open(host, res, opts) {
        var THREE = root.THREE;
        if (!THREE) throw new Error('three_missing');
        opts = opts || {};
        var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
        renderer.setPixelRatio(Math.min(2, root.devicePixelRatio || 1));
        host.appendChild(renderer.domElement);
        renderer.domElement.className = 'sdm-3d-canvas';
        var scene = new THREE.Scene();
        var camera = new THREE.PerspectiveCamera(45, 1, 0.5, 200000);
        scene.add(new THREE.HemisphereLight(0xdff0ff, 0x203040, 0.85));
        var sun = new THREE.DirectionalLight(0xffffff, 0.75);
        sun.position.set(-1, 2, -1);
        scene.add(sun);
        var group = new THREE.Group();
        scene.add(group);
        var mesh = null, water = null, model = null;
        var exag = opts.exaggeration || 6;
        var controls = THREE.OrbitControls ? new THREE.OrbitControls(camera, renderer.domElement) : null;

        function render() { renderer.render(scene, camera); }
        function setModel(r, o) {
            if (mesh) { group.remove(mesh); mesh.geometry.dispose(); mesh.material.dispose(); }
            if (water) { group.remove(water); water.geometry.dispose(); water.material.dispose(); }
            model = build(THREE, r, o || opts);
            mesh = new THREE.Mesh(model.geom, new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide }));
            group.add(mesh);
            water = new THREE.Mesh(new THREE.PlaneGeometry(model.sizeX, model.sizeZ), new THREE.MeshBasicMaterial({ color: 0x58a6ff, transparent: true, opacity: 0.12, depthWrite: false, side: THREE.DoubleSide }));
            water.rotation.x = -Math.PI / 2;
            group.add(water);
            group.scale.set(1, exag, 1);
            var span = Math.max(model.sizeX, model.sizeZ);
            camera.position.set(-span * 0.7, span * 0.7, span * 0.95);
            camera.near = span / 2000; camera.far = span * 20; camera.updateProjectionMatrix();
            if (controls) { controls.target.set(0, -model.depth * exag * 0.35, 0); controls.update(); }
            else camera.lookAt(0, -model.depth * exag * 0.35, 0);
            render();
        }
        function resize() {
            var w = host.clientWidth || 600, h = host.clientHeight || 400;
            renderer.setSize(w, h, false);
            renderer.domElement.style.width = '100%';
            renderer.domElement.style.height = '100%';
            camera.aspect = w / h; camera.updateProjectionMatrix();
            render();
        }
        if (controls) {
            controls.enableDamping = false;
            controls.screenSpacePanning = true;
            controls.addEventListener('change', render);
        }
        var ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
        if (ro) ro.observe(host);
        setModel(res, opts);
        resize();
        return {
            update: function (r, o) { opts = o || opts; setModel(r, opts); },
            setExaggeration: function (z) { exag = z; group.scale.set(1, exag, 1); if (model && controls) { controls.target.set(0, -model.depth * exag * 0.35, 0); controls.update(); } render(); },
            resize: resize,
            snapshot: function () { render(); return renderer.domElement; },
            dispose: function () {
                if (ro) ro.disconnect();
                if (controls) controls.dispose();
                if (mesh) { mesh.geometry.dispose(); mesh.material.dispose(); }
                if (water) { water.geometry.dispose(); water.material.dispose(); }
                renderer.dispose();
                if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
            }
        };
    }

    root.SDM3D = { open: open, _build: build };
})(typeof self !== 'undefined' ? self : globalThis);
